import { useState } from "react";

// ---------------------------------------------------------------------------
// DateRangeCalendar — two-month range picker with a preset rail, matching the
// Unbox app's date picker. Monday-start weeks, purple range band, presets on
// the left, Reset / Cancel / Apply in the footer.
//
// Pure client component. Emits ISO YYYY-MM-DD strings via onApply.
// ---------------------------------------------------------------------------

// ---- date helpers (all local time — never toISOString, which is UTC) -------
const pad = (n: number) => String(n).padStart(2, "0");
const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const sameDay = (a: Date, b: Date) => fmt(a) === fmt(b);
// Monday-start weekday index: 0 = Mon … 6 = Sun
const dow = (d: Date) => (d.getDay() + 6) % 7;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

// ---- presets ---------------------------------------------------------------
// All ranges use complete days (ending yesterday) so a report never includes a
// partial current day, and the prior-period comparison stays apples-to-apples.
type Preset = { label: string; range: (today: Date) => [Date, Date] };

const PRESETS: Preset[] = [
  { label: "Last 7 days", range: (t) => [addDays(t, -7), addDays(t, -1)] },
  {
    label: "Last week",
    range: (t) => {
      const lastSun = addDays(t, -dow(t) - 1);
      return [addDays(lastSun, -6), lastSun];
    },
  },
  { label: "Last 30 days", range: (t) => [addDays(t, -30), addDays(t, -1)] },
  {
    label: "Last calendar month",
    range: (t) => {
      const lm = addMonths(t, -1);
      return [startOfMonth(lm), endOfMonth(lm)];
    },
  },
  { label: "This month", range: (t) => [startOfMonth(t), addDays(t, -1)] },
  { label: "Last 90 days", range: (t) => [addDays(t, -90), addDays(t, -1)] },
];

const DEFAULT_PRESET = "Last 30 days";
const defaultPreset = () => PRESETS.find((p) => p.label === DEFAULT_PRESET) as Preset;

export default function DateRangeCalendar({
  title,
  initialStart,
  initialEnd,
  maxDate,
  onApply,
  onCancel,
}: {
  title?: string;
  initialStart?: string;
  initialEnd?: string;
  /** Latest date the warehouse has data for. Caps the picker and anchors
   *  presets (so "Last 7 days" ends on maxDate, not yesterday). */
  maxDate?: string;
  onApply: (start: string, end: string) => void;
  onCancel: () => void;
}) {
  // When maxDate is set, treat (maxDate + 1) as the picker's "today" anchor so
  // every preset that ends on "yesterday" naturally clamps to maxDate. When
  // not set, fall back to the real today.
  const max = maxDate ? parse(maxDate) : null;
  const today = max ? addDays(max, 1) : new Date();
  const seed = defaultPreset().range(today);

  const [start, setStart] = useState<Date>(initialStart ? parse(initialStart) : seed[0]);
  const [end, setEnd] = useState<Date | null>(initialEnd ? parse(initialEnd) : seed[1]);
  const [hover, setHover] = useState<Date | null>(null);
  // Right-hand month shown; left-hand is one month earlier.
  const [rightMonth, setRightMonth] = useState<Date>(startOfMonth(end ?? start));

  // While picking the end, preview the range up to the hovered day.
  const previewEnd = end ?? hover;
  const [lo, hi] = previewEnd && previewEnd < start ? [previewEnd, start] : [start, previewEnd];

  const inRange = (d: Date) => !!hi && d >= lo && d <= hi;
  const isStart = (d: Date) => sameDay(d, lo);
  const isEnd = (d: Date) => !!hi && sameDay(d, hi);
  const isDisabled = (d: Date) => !!max && d > max;

  const pick = (d: Date) => {
    if (isDisabled(d)) return;
    if (end || d < start) {
      // start a fresh range
      setStart(d);
      setEnd(null);
    } else {
      setEnd(d);
    }
  };

  const applyPreset = (p: Preset) => {
    const [s, e] = p.range(today);
    setStart(s);
    setEnd(e);
    setHover(null);
    setRightMonth(startOfMonth(e));
  };

  const reset = () => applyPreset(defaultPreset());

  const activePreset = end
    ? PRESETS.find((p) => {
        const [s, e] = p.range(today);
        return sameDay(s, start) && sameDay(e, end);
      })?.label
    : undefined;

  const valid = !!end && start <= end;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid var(--color-border-secondary)",
        borderRadius: 14,
        boxShadow: "var(--shadow-md, var(--shadow-sm))",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        overflowX: "auto",
        width: "fit-content",
        maxWidth: "100%",
      }}
    >
      {(title || maxDate) && (
        <div
          style={{
            padding: "13px 18px",
            borderBottom: "1px solid var(--color-border-tertiary)",
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          {title && <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>{title}</div>}
          {maxDate && (
            <div style={{ fontSize: 12, color: "var(--color-text-quaternary)" }}>
              Data through <strong style={{ color: "var(--color-text-secondary)", fontWeight: 600 }}>{maxDate}</strong>
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {/* Preset rail */}
        <div
          style={{
            width: 160,
            flexShrink: 0,
            borderRight: "1px solid var(--color-border-tertiary)",
            padding: "14px 10px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {PRESETS.map((p) => {
            const active = p.label === activePreset;
            return (
              <button
                key={p.label}
                onClick={() => applyPreset(p)}
                style={{
                  textAlign: "left",
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  color: active ? "var(--color-brand-700)" : "var(--color-text-secondary)",
                  background: active ? "var(--color-brand-50)" : "transparent",
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Calendars + footer */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", gap: 16, padding: "16px 18px 8px" }}>
            <MonthGrid
              month={addMonths(rightMonth, -1)}
              navLeft={() => setRightMonth(addMonths(rightMonth, -1))}
              inRange={inRange}
              isStart={isStart}
              isEnd={isEnd}
              isDisabled={isDisabled}
              onPick={pick}
              onHover={setHover}
            />
            <MonthGrid
              month={rightMonth}
              navRight={() => setRightMonth(addMonths(rightMonth, 1))}
              inRange={inRange}
              isStart={isStart}
              isEnd={isEnd}
              isDisabled={isDisabled}
              onPick={pick}
              onHover={setHover}
            />
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              borderTop: "1px solid var(--color-border-tertiary)",
            }}
          >
            <button onClick={reset} style={btnGhost}>
              Reset to last 30 days
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={onCancel} style={btnGhost}>
                Cancel
              </button>
              <button
                onClick={() => valid && end && onApply(fmt(start), fmt(end))}
                disabled={!valid}
                style={{
                  ...btnBase,
                  color: "#fff",
                  background: valid ? "var(--color-brand-600)" : "var(--color-gray-200)",
                  cursor: valid ? "pointer" : "not-allowed",
                  border: "none",
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One month grid
// ---------------------------------------------------------------------------
function MonthGrid({
  month,
  navLeft,
  navRight,
  inRange,
  isStart,
  isEnd,
  isDisabled,
  onPick,
  onHover,
}: {
  month: Date;
  navLeft?: () => void;
  navRight?: () => void;
  inRange: (d: Date) => boolean;
  isStart: (d: Date) => boolean;
  isEnd: (d: Date) => boolean;
  isDisabled: (d: Date) => boolean;
  onPick: (d: Date) => void;
  onHover: (d: Date | null) => void;
}) {
  const first = startOfMonth(month);
  const leading = dow(first); // blank cells before day 1
  const days = endOfMonth(month).getDate();
  const cells: (Date | null)[] = [
    ...Array(leading).fill(null),
    ...Array.from({ length: days }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div style={{ width: 256 }}>
      {/* Header: title with the nav arrow on whichever side this month owns */}
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 28, marginBottom: 8 }}
      >
        <span style={{ width: 24 }}>{navLeft && <NavBtn dir="‹" onClick={navLeft} />}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)" }}>
          {MONTHS[month.getMonth()]} {month.getFullYear()}
        </span>
        <span style={{ width: 24, textAlign: "right" }}>{navRight && <NavBtn dir="›" onClick={navRight} />}</span>
      </div>

      {/* Weekday header */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            style={{
              textAlign: "center",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--color-text-quaternary)",
              padding: "4px 0",
            }}
          >
            {w}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {cells.map((d, i) => {
          // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional by construction
          if (!d) return <div key={i} style={{ height: 40 }} />;
          const within = inRange(d);
          const sStart = isStart(d);
          const sEnd = isEnd(d);
          const selected = sStart || sEnd;
          const disabled = isDisabled(d);
          const col = dow(d);
          // Per-row rounded band: round the left edge at the range start or the
          // start of a week, the right edge at the range end or end of a week.
          const leftRound = sStart || col === 0;
          const rightRound = sEnd || col === 6;
          return (
            <div
              key={fmt(d)}
              style={{
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: !disabled && within ? "var(--color-gray-100)" : "transparent",
                borderTopLeftRadius: leftRound ? 20 : 0,
                borderBottomLeftRadius: leftRound ? 20 : 0,
                borderTopRightRadius: rightRound ? 20 : 0,
                borderBottomRightRadius: rightRound ? 20 : 0,
              }}
            >
              <button
                onClick={() => !disabled && onPick(d)}
                onMouseEnter={() => !disabled && onHover(d)}
                onMouseLeave={() => onHover(null)}
                disabled={disabled}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: "none",
                  cursor: disabled ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: selected ? 700 : within && !disabled ? 600 : 400,
                  color: disabled ? "var(--color-text-quaternary)" : selected ? "#fff" : "var(--color-text-primary)",
                  background: !disabled && selected ? "var(--color-brand-600)" : "transparent",
                  opacity: disabled ? 0.4 : 1,
                  transition: "background 0.1s",
                }}
                title={disabled ? "No warehouse data available yet for this date" : undefined}
              >
                {d.getDate()}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NavBtn({ dir, onClick }: { dir: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label={dir === "‹" ? "Previous month" : "Next month"}
      style={{
        width: 24,
        height: 24,
        lineHeight: "20px",
        fontSize: 18,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        color: "var(--color-text-tertiary)",
        fontFamily: "inherit",
        padding: 0,
      }}
    >
      {dir}
    </button>
  );
}

const btnBase: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 18px",
  borderRadius: 8,
  fontFamily: "inherit",
};
const btnGhost: React.CSSProperties = {
  ...btnBase,
  fontWeight: 500,
  background: "transparent",
  color: "var(--color-text-secondary)",
  border: "1px solid var(--color-border-secondary)",
  cursor: "pointer",
};
